<template>
    <div class="p-4">
        <div class="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
            <div>
                <h4 class="text-gray-800 font-weight-bold m-0"><i class="fas fa-folder-open mr-2 text-teal"></i>Archivo</h4>
                <p class="text-muted small m-0">Expediente digital, documentos y avisos particulares.</p>
            </div>
            <button class="btn btn-teal shadow-sm animated fadeIn" @click="showUploadForm = !showUploadForm" v-can="'user_documents'">
                <i :class="showUploadForm ? 'fas fa-times' : 'fas fa-plus'" class="mr-2"></i>
                {{ showUploadForm ? 'Cancelar' : 'Subir Documento' }}
            </button>
        </div>

        <!-- Upload Form (Permission restricted) -->
        <div v-if="showUploadForm" v-can="'user_documents'" class="card shadow-sm border-light mb-4 animated slideInDown">
            <div class="card-body bg-light">
                <h6 class="font-weight-bold text-teal mb-3">Subir nuevo documento</h6>
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="form-label font-weight-bold">Nombre del Documento</label>
                        <input v-model="form.name" type="text" class="form-control" placeholder="Ej: Resultados Psicométricos">
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label font-weight-bold">Categoría</label>
                        <select v-model="form.type" class="form-control custom-select">
                            <option value="" disabled>Seleccionar categoría...</option>
                            <option value="Psicométrico">Psicométrico</option>
                            <option value="Administrativo">Administrativo</option>
                            <option value="Contrato">Contrato</option>
                            <option value="Aviso">Aviso</option>
                            <option value="Otro">Otro</option>
                        </select>
                    </div>
                    <div class="col-md-12 mb-3">
                        <label class="form-label font-weight-bold">Comentarios/Anotaciones</label>
                        <textarea v-model="form.description" class="form-control" rows="2" placeholder="Información adicional para el empleado..."></textarea>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label font-weight-bold">Archivo</label>
                        <input type="file" ref="fileInput" class="form-control-file border p-2 rounded w-100 bg-white" @change="handleFileChange">
                    </div>
                    <div class="col-md-6 mb-3 d-flex align-items-center">
                        <div class="custom-control custom-switch mt-4">
                            <input type="checkbox" v-model="form.notify_teams" class="custom-control-input" id="notifyTeamsSwitch">
                            <label class="custom-control-label font-weight-bold" for="notifyTeamsSwitch">
                                <i class="fab fa-microsoft mr-1"></i>Notificar por Teams
                            </label>
                        </div>
                    </div>
                </div>
                <div class="text-right">
                    <button class="btn btn-teal px-4" :disabled="loading" @click="uploadDocument">
                        <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
                        {{ loading ? 'Subiendo...' : 'Guardar en Archivo' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Documents Table -->
        <div class="table-responsive rounded-lg shadow-sm border border-light bg-white">
            <table class="table table-hover mb-0 custom-table">
                <thead class="bg-light-gray">
                    <tr>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0 pl-4">Documento</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Categoría</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Comentarios</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0">Fecha</th>
                        <th class="text-uppercase text-secondary font-weight-bold text-xs border-0 text-right pr-4">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="doc in documents" :key="doc.id">
                        <td class="align-middle pl-4 font-weight-bold text-dark">
                            <div class="d-flex align-items-center">
                                <i :class="getFileIcon(doc.filename)" class="mr-3 text-teal fa-lg"></i>
                                <div>
                                    <div class="m-0 d-flex align-items-center">
                                        {{ doc.name }}
                                        <i v-if="doc.sync_status === 'synced'" class="fas fa-cloud ml-2 text-success small" title="Sincronizado con OneDrive"></i>
                                        <i v-else class="fas fa-hdd ml-2 text-warning small" title="Respaldo Local"></i>
                                    </div>
                                    <div class="text-muted small font-weight-normal">
                                        {{ doc.filename }} 
                                        <span v-if="doc.creator" class="ml-2 py-0 px-1 bg-light border-left">
                                            <i class="fas fa-user-edit mr-1 text-xs"></i>{{ doc.creator.name }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="align-middle text-muted">
                            <span class="badge badge-light px-2 py-1 border text-teal">{{ doc.type || 'N/A' }}</span>
                        </td>
                        <td class="align-middle">
                            <span v-if="doc.description" class="text-muted small d-inline-block text-truncate" style="max-width: 200px;" :title="doc.description">
                                {{ doc.description }}
                            </span>
                            <span v-else class="text-gray-300 small">Sin comentarios</span>
                        </td>
                        <td class="align-middle text-muted small">
                            {{ formatDate(doc.created_at) }}
                        </td>
                        <td class="align-middle text-right pr-4">
                            <div class="btn-group shadow-sm rounded">
                                <button class="btn btn-white btn-sm text-teal" @click="downloadDocument(doc)" title="Ver/Descargar">
                                    <i class="fas fa-eye"></i>
                                </button>
                                <button class="btn btn-white btn-sm text-danger" @click="deleteConfirm(doc)" title="Eliminar" v-can="'user_documents'">
                                    <i class="fas fa-trash-alt"></i>
                                </button>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="documents.length === 0">
                        <td colspan="5" class="text-center py-5 text-muted">
                            <div class="d-flex flex-column align-items-center">
                                <i class="fas fa-folder-open fa-3x mb-3 text-gray-200"></i>
                                <p class="m-0 font-weight-bold">No hay documentos en este archivo.</p>
                                <p class="text-muted small">rrhh puede subir psicométricos o avisos aquí.</p>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import request from '@/services/request';
import moment from 'moment';

export default {
    props: ['userId'],
    data() {
        return {
            documents: [],
            showUploadForm: false,
            loading: false,
            form: {
                name: '',
                description: '',
                type: '',
                file: null,
                notify_teams: false
            }
        };
    },
    computed: {
        // No extra computed at this level
    },
    watch: {
        userId: {
            immediate: true,
            handler(val) {
                if (val) this.fetchDocuments();
            }
        }
    },
    methods: {
        async fetchDocuments() {
            try {
                const { data } = await request.get(`/user-documents/${this.userId}`);
                this.documents = data;
            } catch (error) {
                console.error(error);
            }
        },
        handleFileChange(e) {
            this.form.file = e.target.files[0];
            if (this.form.file && !this.form.name) {
                // Auto-fill name if empty
                this.form.name = this.form.file.name.split('.').slice(0, -1).join('.');
            }
        },
        async uploadDocument() {
            if (!this.form.file || !this.form.name || !this.form.type) {
                this.$swal({ 
                    icon: 'warning', 
                    title: 'Campos incompletos', 
                    text: 'Por favor asigna un nombre, selecciona una categoría y un archivo.' 
                });
                return;
            }

            this.loading = true;
            try {
                const formData = new FormData();
                formData.append('user_id', this.userId);
                formData.append('name', this.form.name);
                formData.append('description', this.form.description);
                formData.append('type', this.form.type);
                formData.append('file', this.form.file);
                formData.append('notify_teams', this.form.notify_teams);

                await request.post('/user-documents', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                this.$swal({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    icon: 'success',
                    title: 'Documento guardado'
                });

                this.resetForm();
                this.fetchDocuments();
            } catch (error) {
                console.error(error);
                this.$swal({ icon: 'error', title: 'Error', text: 'No se pudo subir el documento.' });
            } finally {
                this.loading = false;
            }
        },
        resetForm() {
            this.form = {
                name: '',
                description: '',
                type: '',
                file: null,
                notify_teams: false
            };
            this.$refs.fileInput.value = '';
            this.showUploadForm = false;
        },
        async deleteConfirm(doc) {
            const result = await this.$swal({
                title: '¿Eliminar documento?',
                text: "Esta acción no se puede deshacer.",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                try {
                    await request.delete(`/user-documents/${doc.id}`);
                    this.fetchDocuments();
                    this.$swal({ toast: true, position: 'top-end', icon: 'success', title: 'Eliminado', showConfirmButton: false, timer: 2000 });
                } catch (error) {
                    console.error(error);
                }
            }
        },
        async downloadDocument(doc) {
            // Si es un link de OneDrive (synced), lo abrimos en nueva pestaña
            if (doc.sync_status === 'synced' || (doc.file_path && doc.file_path.startsWith('http'))) {
                 window.open(doc.file_path + "?web=1", '_blank');
                 return;
            }

            // Si es local (pending), usamos Axios para enviar el token en la cabecera
            try {
                const response = await request.get(`/user-documents/download/${doc.id}`, {}, { responseType: 'blob' });
                
                if (response.data) {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', doc.filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
            } catch (error) {
                console.error('Download error:', error);
                this.$swal({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    icon: 'error',
                    title: 'Error al descargar el archivo'
                });
            }
        },
        getFileIcon(filename) {
            if (!filename) return 'fas fa-file';
            const ext = filename.split('.').pop().toLowerCase();
            if (ext === 'pdf') return 'fas fa-file-pdf';
            if (['doc', 'docx'].includes(ext)) return 'fas fa-file-word';
            if (['xls', 'xlsx'].includes(ext)) return 'fas fa-file-excel';
            if (['png', 'jpg', 'jpeg'].includes(ext)) return 'fas fa-file-image';
            return 'fas fa-file-alt';
        },
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY');
        }
    }
};
</script>

<style scoped>
.text-teal { color: #0f766e !important; }
.btn-teal {
    background-color: #0f766e;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 12px;
    font-weight: 600;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.btn-teal:hover {
    background-color: #0d6e66;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 118, 110, 0.25);
}
.bg-light-gray { background-color: #f8fafc; }
.custom-table th {
    letter-spacing: 0.05em;
    font-size: 0.7rem;
    padding-top: 1rem;
    padding-bottom: 1rem;
}
.custom-table td {
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
}
.badge-light {
    background-color: #f1f5f9;
    color: #64748b;
    font-weight: 600;
    font-size: 0.75rem;
}
.shadow-sm { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.hover-scale:hover { transform: scale(1.05); }

/* Animaciones */
.animated { animation-duration: 0.4s; animation-fill-mode: both; }
@keyframes slideInDown {
    from { transform: translate3d(0, -20px, 0); opacity: 0; }
    to { transform: translate3d(0, 0, 0); opacity: 1; }
}
.slideInDown { animation-name: slideInDown; }
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
.fadeIn { animation-name: fadeIn; }
</style>
